import "client-only"

import { AppChannel, Channel } from "@/config/channel"
import useSurveyStore from "@/store/survey-store"
import { UAParser } from "ua-parser-js"
import { CookieConsent, getOneTrustCookieConsent } from "../oneTrust"

export type TriggerType = "cta" | "auto"

/**
 * Triggers a survey only once per session.
 *
 * @param triggerSurveyFn
 * @returns boolean indicating whether the survey was triggered
 */
export function triggerSurveyOnce(triggerSurveyFn: () => boolean) {
  if (useSurveyStore.getState().hasSurveyBeenTriggered) {
    return false
  }

  useSurveyStore.getState().markSurveyAsTriggered()

  return triggerSurveyFn()
}

export function triggerSurvey(
  apiUrl: string,
  surveyId: number,
  channel: Channel,
  triggerType: TriggerType,
  ipAddress: string | null,
  accountId?: string | null,
) {
  if (!canTriggerSurvey()) {
    return false
  }

  try {
    window.VocSignal_SendEvent("SetParameters", {
      apiUrl,
      language: document.documentElement.lang ?? "es",
      surveyId,
      metadata: getMetadata(
        surveyId,
        channel,
        triggerType,
        ipAddress,
        accountId,
      ),
    })

    window.VocSignal_ListenEvent?.("SetParametersCallback", () => {
      window.VocSignal_SendEvent("PopupSurvey")
    })
    return true
  } catch (error) {
    console.error("Error triggering survey:", error)
    return false
  }
}

function getMetadata(
  surveyId: number,
  channel: Channel,
  triggerType: TriggerType,
  ipAddress: string | null,
  accountId?: string | null,
) {
  const uaParser = new UAParser()

  const device = uaParser.getDevice()
  const os = uaParser.getOS()
  const isApp = channel !== AppChannel.PortaleWebFullResponsiveWwwSisalIt

  return {
    PROPERTY: isApp ? "app" : "web",
    DEVICE_VENDOR: device.vendor,
    SCREEN_RESOLUTION: `${window.screen.width}x${window.screen.height}`,
    OPERATING_SYSTEM: os.name,
    BROWSER_LANGUAGE: navigator.language,
    ...(accountId != null && { ACCOUNT_ID: accountId }),
    LANGUAGE: document.documentElement.lang ?? "es",
    DEVICE_MODEL: device.model,
    FORM_ID: surveyId.toString(),
    LOGIN_STATUS: "logged_out",
    TRIGGER_TYPE: triggerType,
    ...(ipAddress != null && { IP_ADDRESS: ipAddress }),
    // OS_VERSION: os.version, // This datum is unreliable
    // COUNTRY, // this would require a dedicated API call to a back-end service, avoid it isn't absolutely necessary
    // REGION, // this would require a dedicated API call to a back-end service, avoid it isn't absolutely necessary
    // CITY, // this would require a dedicated API call to a back-end service, avoid it isn't absolutely necessary
  } as const
}

function canTriggerSurvey() {
  return (
    window.SANDSIV_ENABLED &&
    getOneTrustCookieConsent(CookieConsent.ProfilationTargeting)
  )
}
